import { useDownloadFile } from '@newfront-insurance/admin-ui/files';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';

import { getFileExtension, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../lib/analytics';
import type { SandboxFile } from '../lib/sources';

interface SandboxFileLinkProps {
  filename: string;
  children: React.ReactNode;
  sandboxFileMap: Map<string, SandboxFile>;
  messageUuid?: string;
}

/**
 * Renders a sandbox file link as a clickable download trigger.
 * Shows a loading/disabled state until the source-document arrives with the fileUuid,
 * then transitions to a clickable link that triggers file download.
 */
export function SandboxFileLink({ filename, children, sandboxFileMap, messageUuid }: SandboxFileLinkProps) {
  const fileData = sandboxFileMap.get(filename);
  const { download, isDownloading } = useDownloadFile({});
  const analytics = useAnalytics();
  const isLoading = !fileData || fileData.isPending || (!fileData.fileUuid && !fileData.failed);
  const isFailed = Boolean(fileData?.failed);
  const content = isLoading ? 'Generating...' : isFailed ? 'Unavailable' : children;

  const handleClick = () => {
    if (fileData?.fileUuid) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
        fileExtension: getFileExtension(filename),
        fileUuid: fileData.fileUuid,
        messageUuid,
        source: 'sandbox_file',
      });
      download(fileData.fileUuid);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading || isDownloading || isFailed}
      className={cn('text-brand-400 hover:underline', {
        'opacity-50': isDownloading,
        'animate-pulse cursor-default': isLoading,
        'cursor-not-allowed no-underline hover:no-underline': isFailed,
      })}
      title={isFailed ? (fileData?.failureReason ?? 'This file is no longer available.') : undefined}
    >
      {content}
    </button>
  );
}
